import bounds from "./missionBounds.json";
import { missionStem, NEUTRAL_IMAGE } from "./missionImages";

/** Écart entre un symbole de mission et son unité, à tout zoom (demande d'Alexis du 04/11/2026 : « Lorsque les
 *  unités apparaissent, leur symbole de mission est superposé à l'unité, ce n'est pas correct. En zoomant, elles
 *  s'affichent correctement »).
 *
 *  Cause : le serveur pose le symbole à une distance fixe sur le terrain (avancé vers sa cible de quelques
 *  centaines de mètres à une dizaine de kilomètres), alors que l'image du symbole et celle de l'unité gardent leur
 *  taille à l'écran. Au zoom où les unités apparaissent, cette distance ne fait plus que quelques pixels : le
 *  symbole retombe sur l'unité.
 *
 *  Règle : le symbole est dessiné, le long de l'axe unité → symbole, à la plus grande de deux distances à l'écran :
 *  celle du serveur (`len`, qui double à chaque niveau de zoom) et la portée `reach` qui dégage l'unité (plus
 *  `GAP_PX`). Au-delà du zoom `clearZoom` où `len` atteint `reach`, il reste exactement au point du serveur (fort
 *  zoom : rendu inchangé). Emprises à l'écran :
 *  - symbole : rectangle de ses pixels visibles (`missionBounds.json`, tools/assets/mission_symbol_bounds.py),
 *    multiplié par son échelle et tourné de `rotation − cap de la carte` (images tournées avec la carte) ; pastille
 *    neutre du niveau compagnie : rectangle de la pastille, d'après la longueur du verbe ;
 *  - unité : cadre APP-6D et échelon au-dessus, mesurés sur milsymbol (`UNIT_BOX`, ami ou ennemi ; désignations
 *    portées sur les côtés exclues), droit à l'écran comme le symbole de l'unité.
 *  `reach` : test exact des deux rectangles par axes séparateurs, borne haute des distances le long de l'axe qui
 *  gardent un chevauchement ; elle ne dépend que du cap de la carte, pas du zoom.
 *
 *  Poussée dessinée : `reach − len·2^zoom` jusqu'à `clearZoom`, nulle au-delà. Une interpolation exponentielle de
 *  base 2 sur le zoom entre deux paliers (`clearZoom − 8` et `clearZoom`) la reproduit exactement
 *  (`translateExpression`), évaluée par le moteur à chaque image, sans recalcul pendant le zoom.
 *
 *  Jamais poussés : les symboles posés sur l'unité (TENIR, BOUCLER, CONTRE-RÉBELLION : aucun écart unité →
 *  symbole), sur la cible (NEUTRALISER, DÉTRUIRE : le symbole marque l'objectif et n'a pas à s'en écarter) et ceux
 *  qui portent un décalage d'image (`offset` : boucle de JALONNER et FREINER centrée sur l'unité, choix d'Alexis du
 *  14/10/2026 ; queue des flèches d'itinéraire posée sur l'unité, registre A129) — leur place à l'écran ne dépend
 *  déjà pas du zoom. Fonctions pures. */

/** Rectangle [x0, y0, x1, y1] en pixels d'écran (y vers le bas), par rapport à un point. */
export type Box = readonly [number, number, number, number];

/** Emprise visible de chaque image (nom ASCII), en points de l'image par rapport à son centre. */
export const MISSION_BOUNDS: Readonly<Record<string, Box>> = bounds as unknown as Record<string, Box>;

/** Cadre et échelon des symboles d'unité (symbols.ts : taille 33 ami, 34 ennemi, liseré de 3), mesurés sur
 *  milsymbol : ami, rectangle de 54 px et échelon de division au-dessus ; ennemi, losange de 54 px. */
export const UNIT_BOX: Readonly<Record<"AMI" | "ENI", Box>> = {
  AMI: [-27, -32, 27, 19],
  ENI: [-27, -40.5, 27, 27],
};

/** Espace laissé entre les deux emprises (px). */
export const GAP_PX = 6;

/** Écart entre les deux paliers de l'interpolation (niveaux de zoom) : en deçà, la poussée vaut `reach` à 1/256
 *  près. */
const SPAN = 8;

/** Monde de Mercator au zoom 0, en pixels (tuiles de 512 px, Mapbox et MapLibre). */
const WORLD = 512;
const mercX = (lon: number) => ((lon + 180) / 360) * WORLD;
const mercY = (lat: number) => {
  const phi = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(Math.PI / 4 + phi / 2)) / Math.PI) / 2) * WORLD;
};

/** Ce que l'écart lit d'un symbole affiché. */
export interface ClearanceSymbol {
  /** Point du symbole [lon, lat]. */
  at: readonly [number, number];
  /** Écart unité → symbole [Δlon, Δlat] (le point de l'unité est `at − lead`) ; null : unité inconnue. */
  lead?: readonly [number, number] | null;
  image: string;
  mission: string;
  rotation: number;
  scale: number;
  offset: readonly [number, number] | null;
  side: string;
}

/** Écart d'un symbole à son unité, au cap de la carte où il a été calculé. */
export interface Clearance {
  /** Axe unité → symbole, unitaire, dans le repère de la carte au nord (Mercator : x vers l'est, y vers le sud). */
  axis: [number, number];
  /** Distance à l'écran, le long de l'axe, qui dégage l'unité (px). */
  reach: number;
  /** Zoom à partir duquel la distance du serveur suffit (poussée nulle). */
  clearZoom: number;
}

const ON_TARGET = new Set(["NEUTRALISER", "DETRUIRE"]);
const plain = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase();

/** Symbole que l'écart peut pousser (voir la règle en tête). */
export function clearable(s: ClearanceSymbol): boolean {
  // Sans unité connue, ou posé sur elle (TENIR, BOUCLER, CONTRE-RÉBELLION : aucun écart).
  if (s.offset !== null || !s.lead || Math.hypot(s.lead[0], s.lead[1]) < 1e-9) return false;
  if (ON_TARGET.has(plain(s.mission))) return false;
  const stem = missionStem(s.image);
  return s.image === NEUTRAL_IMAGE || !(stem.startsWith("Neut") || stem.startsWith("Det"));
}

/** Emprise de la pastille neutre (missionImages.drawNeutralChip : 40 px @2x de haut, texte gras de 22 px plus 32 px
 *  de marge), en points, d'après la longueur du verbe affiché (largeur de lettre majuscule majorée). */
export function chipBox(label: string): Box {
  const half = Math.max(40, label.length * 15 + 32) / 4;
  return [-half, -10, half, 10];
}

/** Coins à l'écran d'une emprise d'image `box` (points), à l'échelle `scale`, tournée de `angle` (degrés, sens
 *  horaire), comme Mapbox / MapLibre la dessinent (`iconPixelOnScreen`). */
export function corners(box: Box, scale: number, angle: number): [number, number][] {
  const a = (angle * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const [x0, y0, x1, y1] = box;
  const pts: [number, number][] = [
    [x0, y0],
    [x1, y0],
    [x1, y1],
    [x0, y1],
  ];
  return pts.map(([x, y]) => [(x * c - y * s) * scale, (x * s + y * c) * scale]);
}

/** Distances le long de `u` (unitaire) où le polygone convexe `poly`, déplacé de t·u, chevauche le rectangle droit
 *  `box` agrandi de `gap` : intervalle ouvert [lo, hi], null s'il ne le chevauche jamais. Axes séparateurs : ceux du
 *  rectangle et les côtés du polygone ; sur chacun, les déplacements qui gardent un chevauchement forment un
 *  intervalle, et les deux formes se chevauchent tant que t est dans tous. */
export function overlapRange(
  poly: readonly [number, number][],
  box: Box,
  u: readonly [number, number],
  gap = 0,
): [number, number] | null {
  const b: Box = [box[0] - gap, box[1] - gap, box[2] + gap, box[3] + gap];
  const boxPts: [number, number][] = [
    [b[0], b[1]],
    [b[2], b[1]],
    [b[2], b[3]],
    [b[0], b[3]],
  ];
  const axes: [number, number][] = [
    [1, 0],
    [0, 1],
  ];
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i]!;
    const q = poly[(i + 1) % poly.length]!;
    const dx = q[0] - p[0];
    const dy = q[1] - p[1];
    if (Math.hypot(dx, dy) > 1e-9) axes.push([-dy, dx]);
  }
  let lo = -Infinity;
  let hi = Infinity;
  for (const n of axes) {
    const proj = (pts: readonly [number, number][]) => pts.map(([x, y]) => x * n[0] + y * n[1]);
    const a = proj(poly);
    const c = proj(boxPts);
    const aMin = Math.min(...a);
    const aMax = Math.max(...a);
    const cMin = Math.min(...c);
    const cMax = Math.max(...c);
    const k = u[0] * n[0] + u[1] * n[1];
    if (Math.abs(k) < 1e-12) {
      if (aMax <= cMin || aMin >= cMax) return null; // séparés sur cet axe, quel que soit t
      continue;
    }
    // Chevauchement sur cet axe : aMin + t·k < cMax et aMax + t·k > cMin.
    const t1 = (cMax - aMin) / k;
    const t2 = (cMin - aMax) / k;
    lo = Math.max(lo, Math.min(t1, t2));
    hi = Math.min(hi, Math.max(t1, t2));
  }
  return lo < hi ? [lo, hi] : null;
}

/** Écart d'un symbole à son unité au cap `bearing` de la carte (degrés) ; null : jamais poussé (voir la règle en
 *  tête), ou jamais sur son unité. `image` : image montée (variante retournée comprise, même géométrie) ; `label` :
 *  verbe porté par la pastille neutre. */
export function clearance(
  s: ClearanceSymbol,
  bearing: number,
  image = s.image,
  label = s.mission,
): Clearance | null {
  if (!clearable(s) || !s.lead) return null;
  const neutral = s.image === NEUTRAL_IMAGE;
  const box = neutral ? chipBox(label) : MISSION_BOUNDS[missionStem(image)];
  if (!box) return null;
  // Écart du serveur au zoom 0, en pixels du monde de Mercator.
  const dx = mercX(s.at[0]) - mercX(s.at[0] - s.lead[0]);
  const dy = mercY(s.at[1]) - mercY(s.at[1] - s.lead[1]);
  const len = Math.hypot(dx, dy);
  if (!(len > 0)) return null;
  const axis: [number, number] = [dx / len, dy / len];
  // Écran : repère de la carte tourné de son cap (vers le haut de l'écran : direction `bearing`).
  const u = screenAxis(axis, bearing);
  // Pastille neutre : rotation 0, tournée avec la carte (`icon-rotation-alignment` « map »).
  const angle = (neutral ? 0 : s.rotation) - bearing;
  const range = overlapRange(corners(box, s.scale, angle), UNIT_BOX[s.side === "ENI" ? "ENI" : "AMI"], u, GAP_PX);
  if (!range || range[1] <= 0) return null;
  const reach = range[1];
  return { axis, reach, clearZoom: Math.log2(reach / len) };
}

/** Poussée du symbole au zoom `zoom` (px, repère de l'écran au cap `bearing`, y vers le bas) : `reach − len·2^zoom`
 *  le long de l'axe, nulle à partir de `clearZoom`. */
export function translateAt(c: Clearance, zoom: number, bearing = 0): [number, number] {
  const push = Math.max(0, c.reach * (1 - 2 ** (zoom - c.clearZoom)));
  const [x, y] = screenAxis(c.axis, bearing);
  return [x * push + 0, y * push + 0];
}

/** Axe du repère de la carte vu à l'écran, carte tournée de `bearing` (degrés). */
function screenAxis(axis: readonly [number, number], bearing: number): [number, number] {
  const b = (bearing * Math.PI) / 180;
  return [axis[0] * Math.cos(b) + axis[1] * Math.sin(b), -axis[0] * Math.sin(b) + axis[1] * Math.cos(b)];
}

/** `icon-translate` du symbole : la poussée de `translateAt`, exacte entre les deux paliers (interpolation
 *  exponentielle de base 2, celle de la distance du serveur à l'écran), constante en deçà (à 1/256 près de `reach`),
 *  nulle au-delà. Mapbox et MapLibre interpolent un tableau composante par composante. */
export function translateExpression(c: Clearance, bearing = 0): unknown[] {
  const from = c.clearZoom - SPAN;
  return [
    "interpolate", ["exponential", 2], ["zoom"],
    from, ["literal", translateAt(c, from, bearing)],
    c.clearZoom, ["literal", [0, 0]],
  ];
}
